import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Box, Divider, LinearProgress, Paper, Tooltip, Typography } from '@weave-mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
export function formatNumber(n) {
    if (n >= 1000000000) {
        const v = n / 1000000000;
        return `${Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1)}B`;
    }
    if (n >= 1000000) {
        const v = n / 1000000;
        return `${Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1)}M`;
    }
    if (n >= 1000) {
        const v = n / 1000;
        return `${Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1)}K`;
    }
    return String(n);
}
const tooltipSlotProps = {
    tooltip: {
        sx: {
            fontSize: '0.8rem',
            lineHeight: 1.6,
            maxWidth: 280,
            px: 1.5,
            py: 1,
            bgcolor: 'grey.800',
            '& .MuiTooltip-arrow': { color: 'grey.800' },
        },
    },
};
export const StatCard = ({ title, titleTooltip, progressColor, rows, action, sx, }) => (_jsxs(Paper, { elevation: 0, sx: (theme) => ({
        p: 3,
        height: '100%',
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        bgcolor: 'background.paper',
        '@media print': {
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
            boxShadow: 'none',
            overflow: 'hidden',
            p: 1.5,
        },
        ...sx,
    }), children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }, children: [_jsx(Typography, { variant: "subtitle1", sx: {
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: '12px',
                        letterSpacing: '0.7px',
                        color: 'text.primary',
                        mb: 1.5,
                    }, children: title }), titleTooltip && (_jsx(Tooltip, { title: titleTooltip, placement: "top", arrow: true, slotProps: tooltipSlotProps, children: _jsx(InfoOutlinedIcon, { sx: { fontSize: 15, color: 'text.disabled', cursor: 'help' } }) })), action && _jsx(Box, { sx: { ml: 'auto' }, children: action })] }), _jsx(Divider, { sx: { mb: 1.5 } }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: rows.map((row, idx) => (_jsxs(React.Fragment, { children: [row.sectionLabel && (_jsx(Typography, { variant: "overline", color: "text.secondary", sx: { lineHeight: 1, mb: -0.5, display: 'block', fontWeight: 600 }, children: row.sectionLabel })), _jsxs(Box, { children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'baseline',
                                    '@media print': { flexDirection: 'column', alignItems: 'flex-start', gap: '1px' },
                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: row.label }), row.labelTooltip && (_jsx(Tooltip, { title: row.labelTooltip, placement: "top", arrow: true, slotProps: tooltipSlotProps, children: _jsx(InfoOutlinedIcon, { sx: {
                                                        fontSize: 13,
                                                        color: 'text.disabled',
                                                        cursor: 'help',
                                                        displayPrint: 'none',
                                                    } }) }))] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }, children: [_jsx(Typography, { variant: "body1", sx: {
                                                    fontWeight: row.mutedValue ? 'normal' : 'bold',
                                                    color: row.valueColor ?? (row.mutedValue ? 'text.secondary' : 'text.primary'),
                                                    '@media print': { fontSize: '0.8rem' },
                                                }, children: row.displayValue ?? formatNumber(row.value) }), row.pct !== undefined && (_jsxs(Typography, { variant: "body2", color: "text.secondary", fontWeight: "bold", sx: { '@media print': { fontSize: '0.75rem' } }, children: ["(", row.pct, "%)"] })), row.trend != null && (_jsxs(Box, { component: "span", sx: {
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    borderRadius: '20px',
                                                    px: 0.75,
                                                    py: 0.25,
                                                    bgcolor: '#f0fdf4',
                                                    color: '#16a34a',
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    ml: 0.5,
                                                }, children: [row.trend >= 0 ? '▲' : '▼', " ", Math.abs(row.trend), "%"] }))] })] }), row.showProgress && row.pct !== undefined && (_jsx(LinearProgress, { variant: "determinate", value: Math.min(row.pct, 100), color: progressColor ?? 'primary', sx: (theme) => ({
                                    mt: 0.75,
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.divider,
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 2,
                                    },
                                }) }))] })] }, `${row.label}-${idx}`))) })] }));
export default StatCard;
